import React, { useCallback, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Form, useFormState } from "../../../../Components/Forms";
import Modal from "../../../../Components/Modal/Modal";
import SidebarV2Automation from "../../../../Components/Sidebar/SidebarV2Automation";
import TabStrip from "../../../../Components/TabStrip/TabStrip";
import ExpressionModal from "../../Expressions/ExpressionModal";
import { useAutomationTestRequestSidebar } from "../AutomationDetailsHelper";
import { baseConfig } from "../NodeHelper";
import { InnerNodeConfigForm } from "../NodesSidebar/NodeConfigForm";
import {
  AutomationSidebarFooter,
  AutomationSidebarHeader
} from "./AutomationSidebar";
import classes from "./AutomationNodes.module.css";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDoubleRight, faEye } from "@fortawesome/pro-light-svg-icons";
import {
  SidebarVisualTypeEnum,
  useSidebarView
} from "../../../../Components/Sidebar/SidebarViewsHelper";
import { TriggerList } from "./AutomationTestResults";
import Dropdown from "../../../../Components/Dropdown/Dropdown";

const InnerFormContent = ({ formState, FormContent, ...rest }) => {
  const { form, handleChange } = formState;
  const formHandleChange = useCallback(
    (newFormValue) => {
      handleChange((oldV) => {
        const update =
          typeof newFormValue === "function"
            ? newFormValue(oldV.form)
            : newFormValue;
        return {
          ...oldV,
          form: {
            ...oldV.form,
            ...update
          }
        };
      });
    },
    [handleChange]
  );

  const innerFormState = useMemo(() => {
    return {
      form: form.form,
      handleChange: formHandleChange,
      setForm: formHandleChange,
      setValidation: () => {}
    };
  }, [form.form, formHandleChange]);

  return (
    <Form formState={innerFormState}>
      <FormContent formState={innerFormState} {...rest} />
    </Form>
  );
};

const NodeBaseSidebar = ({
  NodeData,
  handleSubmit,
  automationTestClick,
  settings,
  CurrentNodeId,
  type,
  updater,
  automationId,
  baseRequest,
  AllElements
}) => {
  const { defaultForm, FormContent, includesSelf } = settings;

  const formState = useFormState(
    () => {
      const baseForm = { ...defaultForm, ...NodeData?.form };

      return {
        form: baseForm,
        config: { ...baseConfig, ...NodeData?.config }
      };
    }
    // validateTaskForm
  );

  const [modalStatus, setModalStatus] = useState(false);
  const ModalOptionsRef = useRef({});
  const openModal = (inputString, value, onChange) => {
    ModalOptionsRef.current = {
      inputString,
      value,
      onChange
    };
    setModalStatus(true);
  };

  const closeModal = () => {
    setModalStatus(false);
  };

  const sidebarConfig = useMemo(() => {
    return {
      submit: handleSubmit,
      OnExpressionModalOpen: openModal,
      defaultForm
    };
  }, [defaultForm, handleSubmit]);

  const onSave = () => {
    handleSubmit({
      form: formState.form.form,
      config: formState.form.config
    });
  };

  const handleOnlySave = useCallback(() => {
    updater &&
      updater(CurrentNodeId, {
        form: formState.form.form,
        config: formState.form.config
      });
  }, [CurrentNodeId, formState.form.config, formState.form.form, updater]);

  const tabs = useMemo(() => {
    if (FormContent) {
      return [
        {
          default: true,
          name: <FormattedMessage id="PARAMETERS" />,
          component: (
            <div className="px-3">
              <InnerFormContent
                automationId={automationId}
                handleOnlySave={handleOnlySave}
                CurrentNodeId={CurrentNodeId}
                FormContent={FormContent}
                formState={formState}
                canEdit
              />
            </div>
          )
        },
        {
          name: <FormattedMessage id="SETTINGS" />,
          component: (
            <div className="px-3">
              <InnerNodeConfigForm formState={formState} />
            </div>
          )
        }
      ];
    } else
      return [
        {
          name: <FormattedMessage id="SETTINGS" />,
          component: (
            <div className="px-3">
              <InnerNodeConfigForm formState={formState} />
            </div>
          )
        }
      ];
  }, [CurrentNodeId, FormContent, automationId, formState, handleOnlySave]);
  const sidebarView = useSidebarView();

  const {
    data,
    loading
    // error
  } = useAutomationTestRequestSidebar(baseRequest);
  const parsedRequestData = JSON.parse(data);

  const [OpenPreview, setOpenPreview] = useState(() => {
    if (!parsedRequestData) return false;
    const { cycles } = parsedRequestData.data.workflowResult
      ? parsedRequestData.data.workflowResult
      : {};
    if (parsedRequestData?.data?.userError && TriggerList.includes(type))
      return true;
    if (!cycles || !cycles[CurrentNodeId]) return false;
    return true;
  });
  return (
    <div className="d-flex h-100 flex-column bg-white">
      <SidebarV2Automation config={sidebarConfig}>
        <Modal enableCloseButton isOpen={modalStatus} onClose={closeModal}>
          <ExpressionModal
            isOpen={modalStatus}
            closeModal={closeModal}
            includesSelf={includesSelf}
            AllElements={AllElements}
            CurrentNodeId={CurrentNodeId}
            ModalOptionsRef={ModalOptionsRef}
          />
        </Modal>
        <div
          className={classnames("d-flex flex-column h-100", {
            [classes.disableSideBarPreview]: !OpenPreview
          })}
        >
          <AutomationSidebarHeader
            formState={formState}
            testLoading={loading}
            automationTestClick={automationTestClick}
            CurrentNodeId={CurrentNodeId}
            baseRequest={baseRequest}
            title={CurrentNodeId}
          />
          <div className="flex-1 of-hidden d-flex">
            <div
              style={{ width: 680, zIndex: 1, paddingTop: 8 }}
              className="shadow"
            >
              <TabStrip
                route={false}
                alignCenter
                headerClassName="px-2"
                contentClassName="px-0"
                items={tabs}
                className="ar-details-tabstrip of-y-auto h-100 flex-1"
              />
            </div>
            <div className="bg-airdesk">
              <div
                className={classnames(
                  "flex-1 bg-airdesk d-flex h-100 flex-column",
                  classes.nodebaseSidebarPreviewSide
                )}
              >
                {OpenPreview ? (
                  <div
                    style={{
                      padding: "14px 15px 15px 15px"
                    }}
                    className="d-flex border-bottom justify-content-between align-items-center"
                  >
                    <div className="text-black text-truncate fs-16">
                      <FontAwesomeIcon icon={faEye} className="mr-2" />
                      <span>
                        <FormattedMessage id="PREVIEW_TEST_RESULTS" />
                      </span>
                    </div>
                    {sidebarView !== SidebarVisualTypeEnum.fullscreen && (
                      <div
                        className="cursor-pointer p-2 ar-entity-summary-message-rounded-button ar-entity-summary-message-send-button"
                        onClick={() => {
                          setOpenPreview((e) => {
                            return !e;
                          });
                        }}
                      >
                        <FontAwesomeIcon
                          rotation={OpenPreview ? 180 : undefined}
                          icon={faChevronDoubleRight}
                          className="text-color-link"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-2 h-100 d-flex justify-content-center align-items-center">
                    <div
                      className="cursor-pointer bg-airdesk ar-entity-summary-message-rounded-button ar-entity-summary-message-send-button"
                      onClick={() => {
                        setOpenPreview((e) => {
                          return !e;
                        });
                      }}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </div>
                  </div>
                )}
                {OpenPreview ? (
                  <TestWorkflowContainerResults
                    type={type}
                    CurrentNodeId={CurrentNodeId}
                    RequestData={parsedRequestData}
                  />
                ) : (
                  <div className="flex-0-0-auto h-100"></div>
                )}
              </div>
            </div>
          </div>
          <div className="of-hidden d-flex">
            <div style={{ width: 680, zIndex: 1 }} className="shadow">
              <AutomationSidebarFooter onClick={onSave} />
            </div>
            <div
              className={classnames(
                "bg-airdesk h-100",
                classes.nodebaseSidebarPreviewSide
              )}
            ></div>
          </div>
        </div>
      </SidebarV2Automation>
    </div>
  );
};
const Item = ({ item }) => {
  return parseInt(item) + 1;
};

const TestWorkflowContainerResults = ({ RequestData, CurrentNodeId, type }) => {
  const intl = useIntl();
  if (!RequestData) return <div className="flex-0-0-auto h-100"></div>;

  if (RequestData.data?.userError && TriggerList.includes(type)) {
    return (
      <div className="flex-0-0-auto h-100">
        <div className="p-3">
          <div className="d-flex mb-2 align-items-center text-danger-alt">
            {RequestData.data.name}
          </div>
          <div className="text-black breakWord">{RequestData.data.message}</div>
        </div>
      </div>
    );
  }

  const { cycles } = RequestData.data.workflowResult
    ? RequestData.data.workflowResult
    : {};

  if (!cycles || !cycles[CurrentNodeId])
    return <div className="flex-0-0-auto h-100"></div>;
  const {
    cyclesCount,
    errorsCount,
    // sucessesCount,
    results
  } = cycles[CurrentNodeId];

  // console.log(cyclesCount, errorsCount, sucessesCount, results);

  if (errorsCount > 0)
    return (
      <div className="overflow-auto flex-1">
        {results.map((e, i) => {
          const { result, error } = e;
          const contents = [];
          const isError = Boolean(error);
          let count = 0;
          const resolvedType = error || result;
          let keysLength = Object.keys(resolvedType).length;
          for (const key in resolvedType) {
            if (Object.hasOwnProperty.call(resolvedType, key)) {
              const element = resolvedType[key];
              if (typeof element === "object") {
                contents.push(
                  <div
                    key={key}
                    className={classnames("pl-2", {
                      "text-danger-alt": isError,
                      "text-black": !isError
                    })}
                  >{`"${key}" : ${handleObjToString(element)}${
                    count !== keysLength - 1 ? ", " : ""
                  }`}</div>
                );
              } else {
                contents.push(
                  <div
                    key={key}
                    className={classnames("pl-2", {
                      "text-danger-alt": isError,
                      "text-black": !isError
                    })}
                  >{`"${key}" : "${element}"${
                    count !== keysLength - 1 ? ", " : ""
                  }`}</div>
                );
              }
              count++;
            }
          }
          return (
            <div key={i} className="border-bottom p-3">
              <div className="d-flex  align-items-center text-black">
                {`${intl.formatMessage({ id: "CYCLE" })} ${i + 1}`}
              </div>
              <div className="text-black pl-2 breakWord">
                <div>{`{`}</div>
                {contents}
                <div>{`}`}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  return (
    <AutomtionTestResultsPreviewWithDropdowForCycles
      cyclesCount={cyclesCount}
      results={results}
    />
  );
  // return (
  //   <div className="overflow-auto flex-1">
  //     {results.map((e, i) => {
  //       const { result } = e;
  //       const contents = [];
  //       let count = 0;
  //       let keysLength = Object.keys(result).length;
  //       for (const key in result) {
  //         if (Object.hasOwnProperty.call(result, key)) {
  //           const element = result[key];
  //           if (typeof element === "object") {
  //             contents.push(
  //               <div
  //                 key={key}
  //                 className="text-black pl-2"
  //               >{`"${key}" : ${handleObjToString(element)}${
  //                 count !== keysLength - 1 ? ", " : ""
  //               }`}</div>
  //             );
  //           } else {
  //             contents.push(
  //               <div
  //                 key={key}
  //                 className="text-black pl-2"
  //               >{`"${key}" : "${element}"${
  //                 count !== keysLength - 1 ? ", " : ""
  //               }`}</div>
  //             );
  //           }
  //           count++;
  //         }
  //       }
  //       return (
  //         <div key={i} className="border-bottom p-3">
  //           <div className="d-flex  align-items-center text-black">
  //             {`${intl.formatMessage({ id: "CYCLE" })} ${i + 1}`}
  //           </div>
  //           <div className="text-black pl-2 breakWord">
  //             <div>{`{`}</div>
  //             {contents}
  //             <div>{`}`}</div>
  //           </div>
  //         </div>
  //       );
  //     })}
  //   </div>
  // );
};

const AutomtionTestResultsPreviewWithDropdowForCycles = ({
  cyclesCount,
  results
}) => {
  const intl = useIntl();
  const [activeCycle, setActiveCycle] = useState(0);
  const Resolvedcycles = useMemo(() => {
    const cycles = [];
    for (var i = 0; i < cyclesCount; i++) {
      cycles.push(i);
    }
    return cycles;
  }, [cyclesCount]);

  const content = useMemo(() => {
    const { result } = results[activeCycle];
    const contents = [];
    let count = 0;
    let keysLength = Object.keys(result).length;

    for (const key in result) {
      if (Object.hasOwnProperty.call(result, key)) {
        const element = result[key];
        if (typeof element === "object") {
          contents.push(
            <div
              key={key}
              className="text-black pl-2"
            >{`"${key}" : ${handleObjToString(element)}${
              count !== keysLength - 1 ? ", " : ""
            }`}</div>
          );
        } else {
          contents.push(
            <div
              key={key}
              className="text-black pl-2"
            >{`"${key}" : "${element}"${
              count !== keysLength - 1 ? ", " : ""
            }`}</div>
          );
        }
        count++;
      }
    }
    return contents;
  }, [activeCycle, results]);

  return (
    <div className="overflow-auto flex-1">
      <div className="border-bottom p-3">
        <div className="d-flex  align-items-center text-black">
          <div className="mr-3">{`${intl.formatMessage({ id: "CYCLE" })}`}</div>
          <Dropdown
            className={classnames("bg-white")}
            // preview={preview}
            closeOnSelect
            closeOnClickWhileOpen
            items={Resolvedcycles}
            onChange={(e) => setActiveCycle(e)}
            listItemComponent={Item}
            valueComponent={Item}
            value={activeCycle}
          />
        </div>
        <div className="text-black pl-2 breakWord">
          <div>{`{`}</div>
          {content}
          <div>{`}`}</div>
        </div>
      </div>
    </div>
  );
};

const handleObjToString = (obj) => {
  let result = [];
  if (Array.isArray(obj)) {
    result.push("[");
    for (let index = 0; index < obj.length; index++) {
      const o = obj[index];
      if (typeof o === "object") {
        result.push("{");
        let count = 0;
        let objKeyLength = Object.keys(o).length;
        for (const key in o) {
          if (Object.hasOwnProperty.call(o, key)) {
            const element = o[key];
            let string = "";
            if (typeof element === "object") {
              string = `"${key}" : ${handleObjToString(element)}`;
            } else {
              string = `"${key}" : "${`${element}`.replace(/"/g, '\\"')}"`;
            }
            if (count !== objKeyLength - 1) string += ",";
            count++;
            result.push(string);
          }
        }
        if (index !== obj.length - 1) {
          result.push("},");
        } else result.push("}");
      } else {
        result.push(o);
        if (index !== obj.length - 1) {
          result.push(",");
        }
      }
    }
    result.push("]");
  } else if (obj && typeof obj === "object") {
    let count = 0;
    let objKeyLength = Object.keys(obj).length;
    result.push("{");
    for (const key in obj) {
      let string = "";
      if (Object.hasOwnProperty.call(obj, key)) {
        const element = obj[key];
        if (typeof element === "object") {
          string = `"${key}" : ${handleObjToString(element)}`;
        } else {
          string = `"${key}" : "${`${element}`.replace(/"/g, '\\"')}"`;
        }
        if (count !== objKeyLength - 1) string += ",";
        count++;
        result.push(string);
      }
    }
    result.push("}");
  }
  return result.join("");
};

export default NodeBaseSidebar;
